import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ComponentType, FaqModel, HxFaqService, SaveFaqRequest } from 'hx-services';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxBrandSelectComponent, HxEnumSelectComponent, HxTagSelectComponent } from 'hx-components';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NgIf } from '@angular/common';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';

@Component({
    selector: 'hc-faq-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxBrandSelectComponent, EditorComponent, HxTagSelectComponent, NzInputNumberComponent, HxEnumSelectComponent, NgIf, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, TranslocoPipe]
})
export class FaqEditComponent implements OnInit, OnDestroy {
  model?: FaqModel;
  isEdit = false;
  isCreate = false;
  form = new FormGroup({
    id: new FormControl<number | null>(null),
    titleRu: new FormControl<string | null>(null, [Validators.required]),
    titleKk: new FormControl<string | null>(null, [Validators.required]),
    brandId: new FormControl<number | null>(null, [Validators.required]),
    seq: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
    component: new FormControl<ComponentType | null>(null, [Validators.required]),
    answerRu: new FormControl<string | null>(null, [Validators.required]),
    answerKk: new FormControl<string | null>(null, [Validators.required]),
    tags: new FormControl<string[] | null>(null),
  });
  isLoading = {
    model: false,
  };
  isSaving = false;
  private readonly path = 'faqs';
  private $destroyed = new Subject<void>();

  constructor(
    protected aRoute: ActivatedRoute,
    protected fb: UntypedFormBuilder,
    protected faqService: HxFaqService,
    protected router: Router,
    protected modal: NzModalService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
  ) {
  }

  ngOnInit(): void {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as FaqModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.isCreate = true;
    }
    this.updateForm();
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.value;
      if (!formModel.brandId || !formModel.titleRu || !formModel.seq || !formModel.answerRu || !formModel.component || !formModel.titleKk || !formModel.answerKk) {
        throw new Error('required fields');
      }
      const model: SaveFaqRequest = {
        id: this.model?.id,
        title: {ru: formModel.titleRu, kk: formModel.titleKk},
        answer: {ru: formModel.answerRu, kk: formModel.answerKk},
        tags: formModel.tags || [],
        brandId: formModel.brandId,
        seq: formModel.seq,
        component: formModel.component,
      };

      this.isSaving = true;

      try {
        const result = await this.faqService.saveFaq(model as SaveFaqRequest);
        this.form.markAsPristine();
        this.notification.create(
          'success',
          this.tr.translate('nav.Faqs'),
          this.tr.translate('nt.dataSaved')
        );
        if (this.isCreate) {
          this.router.navigate([`/${this.path}/${result.id}/edit`]);
        } else {
          this.isLoading.model = true;
          this.faqService.getFaqById(result.id).then(savedModel => {
            this.model = savedModel;
            this.isLoading.model = false;
            this.updateForm();
          });
        }
      } catch (err: any) {
        this.notification.error(this.tr.translate('nav.Faqs'), this.tr.translate(err.error?.message));
      } finally {
        this.isSaving = false;
      }
    } else {
      Object.entries(this.form.controls).forEach(([key, control]) => {
        if (control.status !== 'VALID') {
          console.log('control is not valid', key, control);
        }
      });
      console.log('not valid form', this.form);
    }
  }

  private updateForm() {
    if (!this.model) {
      return;
    }
    this.form.patchValue({
      id: this.model.id,
      titleRu: this.model.title.ru,
      titleKk: this.model.title.kk,
      answerRu: this.model.answer?.ru,
      answerKk: this.model.answer?.kk,
      brandId: this.model.brandId,
      seq: this.model.seq,
      tags: this.model.tags,
      component: this.model.component
    });


  }

  protected readonly ComponentType = ComponentType;
}
